import type { BaseQueryApi, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { type AxiosError, AxiosHeaders, type AxiosRequestConfig } from 'axios';
import axiosClientRequest from '@/clients/axiosClient';

interface AxiosBaseQueryExtraOptions {
  logResponse?: boolean;
}

interface AxiosBaseQueryArgs {
  baseUrl: string;
  prepareHeaders?: (headers: AxiosHeaders, api: BaseQueryApi) => AxiosHeaders;
}

interface AxiosBaseQueryParams {
  url: string;
  method: AxiosRequestConfig['method'];
  body?: unknown;
  params?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * The RTK base query is a wrapper for fetch. If we want to continue using axios and interceptors we need to make our own wrapper
 * to work with RTK createApi tooling.
 * https://medium.com/@aliozzkan/rtk-query-and-axios-a-custom-base-query-for-powerful-and-flexible-data-fetching-960588a8e668
 */
export const axiosBaseQuery =
  ({
    baseUrl,
    prepareHeaders,
  }: AxiosBaseQueryArgs): BaseQueryFn<
    AxiosBaseQueryParams,
    unknown,
    unknown,
    AxiosBaseQueryExtraOptions
  > =>
  async ({ url, method, body: data, params, ...rest }, api, extraOptions) => {
    const headers =
      prepareHeaders?.(new AxiosHeaders(), api) ?? new AxiosHeaders();

    try {
      const result = await axiosClientRequest({
        url,
        baseURL: baseUrl,
        method,
        data,
        params,
        headers,
        ...rest,
        signal: api.signal,
      });

      if (extraOptions?.logResponse) {
        console.log('Response:', {
          data: result.data,
          status: result.status,
          headers: result.headers,
        });
      }

      return {
        data: result.data,
        meta: {
          headers: result.headers,
          status: result.status,
          config: result.config,
          request: result.request,
        },
      };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
          headers: err.response?.headers,
        },
      };
    }
  };
